<template>
    <div class="slider-container" ref="slider" 
        @touchstart="startTouch" 
        @touchmove="moveTouch" 
        @touchend="endDrag"
        @mousedown="startDrag" 
        @mousemove="moveDrag" 
        @mouseup="endDrag" 
        @mouseleave="endDrag">
        
        <!-- Apply the computed sliderStyle here -->
        <div class="slider-content" ref="content" :style="sliderStyle">
            <!-- Loop through the products and display each product as a slider item -->
            <div v-for="(item, id) in products" :key="id" class="product-slider-item">
                <img :src="getImageUrl(item.image_url)" :alt="item.name" class="product-image" @error="handleImageError">
                <div class="product-name">{{ item.name }}</div>
                <div class="product-points points-pad">{{ item.points }} PONTOS</div>
                <div v-if="isLeader !== 2">
                    <div class="individual-units">{{ item.units }} Unidades</div>
                </div>
            </div>
        </div>
    </div>
</template>

  
<script>
export default {
    props: {
        products: {
            type: Array,
            required: true,
        },
        getImageUrl: {
            type: Function,
            required: true,
        },
        handleImageError: {
            type: Function,
            required: true,
        },
        isLeader: {
            type: Function,
            required: true,
        }
    },
    data() {
        return {
            isDragging: false,
            startX: 0,
            currentTranslate: 0,
            prevTranslate: 0,
            maxTranslate: 0,
            minTranslate: 0,
            startTime: 0,
            velocity: 0,
            autoSlideInterval: null,
        };
    },
    computed: {
        sliderStyle() {
            return {
                transform: `translateX(${this.currentTranslate}px)`,
                transition: this.isDragging ? 'none' : 'transform 0.4s ease-out',
            };
        },
    },
    mounted() {
        this.calculateBoundaries();
        window.addEventListener('resize', this.calculateBoundaries);
        this.startAutoSlide();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.calculateBoundaries);
        this.stopAutoSlide();
    },
    methods: {
        calculateBoundaries() {
            const containerWidth = this.$refs.slider.offsetWidth;
            const contentWidth = this.$refs.content.scrollWidth;
            this.maxTranslate = 0;
            const bufferSpace = 1;
            this.minTranslate = containerWidth - contentWidth - bufferSpace;
        },
        startAutoSlide() {
            if (this.autoSlideInterval) return; // Prevent starting another interval if one is already running

            this.autoSlideInterval = setInterval(() => {
                const slideAmount = -205; // Adjust the amount to slide each interval
                this.currentTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, this.currentTranslate + slideAmount));
                this.prevTranslate = this.currentTranslate;

                // Stop sliding if reached the last element
                if (this.currentTranslate <= this.minTranslate) {
                    this.stopAutoSlide();
                }
            }, 3000); // Adjust the interval time (e.g., 3000ms = 3 seconds)
        },
        stopAutoSlide() {
            if (this.autoSlideInterval) {
                clearInterval(this.autoSlideInterval);
                this.autoSlideInterval = null;
            }
        },
        startTouch(event) {
            this.stopAutoSlide();
            this.startX = event.touches[0].clientX;
            this.startTime = new Date().getTime();
            this.isDragging = true;
        },
        moveTouch(event) {
            if (!this.isDragging) return;
            event.preventDefault();
            const currentX = event.touches[0].clientX;
            const movementX = currentX - this.startX;
            this.currentTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, this.prevTranslate + movementX));
        },
        startDrag(event) {
            event.preventDefault();
            this.stopAutoSlide();
            this.startX = event.clientX;
            this.startTime = new Date().getTime();
            this.isDragging = true;
        },
        moveDrag(event) {
            if (!this.isDragging) return;
            const currentX = event.clientX;
            const movementX = currentX - this.startX;
            this.currentTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, this.prevTranslate + movementX));
        },
        endDrag() {
            if (this.isDragging) {
                this.isDragging = false;
                const endTime = new Date().getTime();
                const elapsedTime = endTime - this.startTime;
                const distanceMoved = this.currentTranslate - this.prevTranslate;
                this.velocity = distanceMoved / elapsedTime;

                // Apply momentum
                const momentumDistance = this.velocity * 200; // Adjust multiplier as needed
                this.currentTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, this.currentTranslate + momentumDistance));

                this.prevTranslate = this.currentTranslate;

                // Optionally restart auto-slide after a delay
                setTimeout(() => {
                    if (!this.isDragging) {
                        this.startAutoSlide();
                    }
                }, 3000); // 3-second delay after user interaction
            }
        }
    }
};
</script>

<style scoped>
.slider-container {
    overflow: hidden;
    cursor: grab;
    min-height: 40px;
    user-select: none;
    margin-right: 30px;
}

.slider-content {
    display: flex;
    font-size: 16px;
    justify-content: space-between !important;
}

.product-slider-item {
    white-space: nowrap;
    padding: 12px 20px;
    text-align: center;
    cursor: pointer;
    border: 1px solid #BFBFBF;
    background-color: white;
    transition: background-color 0.3s ease;
    margin-left: 10px;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    min-width: 196px;
    min-height: 256px;
}

.product-name {
    height: 3em;
    font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  line-height: 1.5em; /* Altura da linha */
  overflow-wrap: break-word; /* Quebra de palavras */
  word-wrap: break-word; /* Quebra de palavras para compatibilidade */
  display: block; /* Permite que o elemento ocupe a largura total */
  white-space: normal; /* Permite quebra de linha */
  margin-bottom: auto; /* Empurra o produto-points para baixo */
}

.selected {
    background-color: #F47325cc;
    color: white;
    border-color: #F47325;
}

.product-slider-item:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
}

.product-slider-item:last-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    /* margin-right: 30px; */
}

</style>
